"use client";

import { AudioQualityText } from "@/app/(application)/(normal)/create/components/output/AudioQualityText";
import { constructConversionMultis } from "@/app/(application)/(normal)/create/components/output/constructConversionMultis";
import { ConversionHistory } from "@/app/(application)/(normal)/create/components/output/ConversionHistory";
import { NoConversions } from "@/app/(application)/(normal)/create/components/output/NoConversions";
import { PersonalUseText } from "@/app/(application)/(normal)/create/components/output/PersonalUseText";
import { useTrackTtvConversionCompletions } from "@/app/(application)/(normal)/create/components/output/useTrackTtvConversionCompletions";
import { AudioPlayerDownloadConversion } from "@/components/audioPlayers/audioPlayerDownloadConversion/AudioPlayerDownloadConversion";
import { AudioPlayerDownloadConversionMulti } from "@/components/audioPlayers/AudioPlayerDownloadConversionMulti/AudioPlayerDownloadConversionMulti";
import { AudioOutputSkeleton } from "@/components/skeletons/audio/AudioOutputSkeleton";
import { Skeleton } from "@/components/skeletons/Skeleton";
import { Heading3 } from "@/components/text/Heading3";
import { SoftText } from "@/components/text/SoftText";
import { SECONDS_CHECK_IF_CONVERSION_UPDATE } from "@/constants/constants";
import { checkConversionsPercentagesDoneAction } from "@/data/conversion/actions/checkConversionsPercentagesDoneAction";
import {
  ConversionConstructedNoDates,
  MixedModelConstructedNoDates,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { useStatusPolling } from "@/utils/hooks/useStatusPolling";
import { isConversionMulti } from "@/utils/isConversionMulti";

const isAnyConversionRunning = (conversions: ConversionConstructedNoDates[]) =>
  conversions.some(
    (conversion) => conversion.confirmedDone !== true && !conversion.failed
  );

const checkConversionsStatus = (conversions: ConversionConstructedNoDates[]) =>
  checkConversionsPercentagesDoneAction({
    conversionsInput: conversions.map((conversion) => ({
      id: conversion.id,
      percentageDone: conversion.percentageDone,
      confirmedDone: conversion.confirmedDone,
      failed: conversion.failed,
    })),
  });

export function Output({
  conversions,
  user,
  models,
  mixedModelsConstructed,
  maxNumberOfConversionsHistory,
  shouldShowLoadState,
}: {
  conversions: ConversionConstructedNoDates[] | null;
  user: UserNoDates | null;
  models: ModelNoDates[] | null;
  mixedModelsConstructed: MixedModelConstructedNoDates[] | null;
  maxNumberOfConversionsHistory: number;
  shouldShowLoadState: boolean;
}) {
  useTrackTtvConversionCompletions(conversions);
  useStatusPolling({
    pollInput: shouldShowLoadState ? null : conversions,
    isRunning: isAnyConversionRunning,
    checkStatus: checkConversionsStatus,
    baseDelayMs: SECONDS_CHECK_IF_CONVERSION_UPDATE * 1000,
    statusLabel: "conversions",
  });

  const conversionHistory:
    | (ConversionConstructedNoDates | ConversionConstructedNoDates[])[]
    | null =
    conversions === null ? null : constructConversionMultis(conversions);

  const shouldShowEmptyOutput =
    user === null ||
    conversionHistory === null ||
    conversionHistory.length === 0;

  return (
    <div>
      <div className="rounded-3xl bg-gray-50 px-4 py-4 shadow-sm ring-1 ring-gray-900/5 sm:px-8 sm:py-8">
        <Heading3 elementType="h3">Output</Heading3>
        {shouldShowLoadState ?
          <Skeleton className="mb-4 mt-1 h-9 w-full rounded-lg" />
        : <SoftText elementType="p" className="mb-4 mt-1">
            Your {maxNumberOfConversionsHistory} last conversions are saved.
            Make sure to download any conversions that you want to keep.
            <PersonalUseText user={user} />
            <AudioQualityText user={user} />
          </SoftText>
        }
        {shouldShowLoadState && <AudioOutputSkeleton size="medium" />}
        {!shouldShowLoadState && shouldShowEmptyOutput && <NoConversions />}
        {!shouldShowLoadState && !shouldShowEmptyOutput && (
          <>
            <div>
              {isConversionMulti(conversionHistory[0]) ?
                <AudioPlayerDownloadConversionMulti
                  key={conversionHistory[0].map((c) => c.id).join("-")}
                  conversionMulti={conversionHistory[0]}
                  mixedModelsConstructed={mixedModelsConstructed}
                  models={models}
                  user={user}
                />
              : <AudioPlayerDownloadConversion
                  key={conversionHistory[0].id}
                  conversion={conversionHistory[0]}
                  models={models}
                  user={user}
                  mixedModelsConstructed={mixedModelsConstructed}
                />
              }
            </div>

            {conversionHistory.length > 1 && (
              <ConversionHistory
                user={user}
                conversionHistory={conversionHistory.slice(1)}
                models={models}
                mixedModelsConstructed={mixedModelsConstructed}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
